import { useCallback, useMemo, useState } from 'react'
import type { ClassificationTuple } from './staging'
import type { PanelAxis } from './types'

export interface RevealedAxes {
	/** Axes to render as pickers right now. */
	shown: PanelAxis[]
	/** Axes still available to add. */
	hidden: PanelAxis[]
	reveal: (axisCode: string) => void
}

/**
 * Splits the registry's axes into "show now" and "available to add".
 *
 * Which axes exist is DATA — a dimension is a row, so the set grows by INSERT
 * with no deploy. Any surface that renders "every axis" therefore gets taller
 * every time the taxonomy grows: today's 15 dimensions already make a
 * render-them-all panel taller than the viewport, and the three that gate a
 * commit carry the same weight as the twelve that do not.
 *
 * So: required axes always, plus any axis configured to open by default, plus
 * whatever the user asked for, plus anything already carrying a value.
 * Everything else waits behind an "add" affordance. An axis with a value stays
 * shown even if it was never explicitly revealed — otherwise inheriting a value
 * from the batch would strand it, visible as a chip but with no control to
 * change it.
 *
 * `defaultAxisCodes` is deliberately distinct from `requiredAxisCodes`: an axis
 * can be worth putting on screen unprompted without being one a file cannot
 * commit without. Required axes gate `readiness`; these do not.
 */
export function useRevealedAxes(
	axes: PanelAxis[],
	requiredAxisCodes: string[],
	values: ClassificationTuple,
	defaultAxisCodes: string[] = [],
): RevealedAxes {
	const [revealed, setRevealed] = useState<string[]>([])

	const reveal = useCallback((axisCode: string) => {
		setRevealed((current) => (current.includes(axisCode) ? current : [...current, axisCode]))
	}, [])

	return useMemo(() => {
		const isShown = (code: string) =>
			requiredAxisCodes.includes(code) ||
			defaultAxisCodes.includes(code) ||
			revealed.includes(code) ||
			Boolean(values[code])

		return {
			shown: axes.filter((axis) => isShown(axis.code)),
			hidden: axes.filter((axis) => !isShown(axis.code)),
			reveal,
		}
	}, [axes, requiredAxisCodes, defaultAxisCodes, revealed, values, reveal])
}
